import React, { useEffect, useState } from 'react';

import { useTheme } from '@/contexts/ThemeContext';
import { Book, titleToSlug } from '@/utils/bookRouting';
import { isNativeApp } from '@/utils/platform';

import CollectionRow from '../components/CollectionRow';
import TopNavigation from '../components/TopNavigation';
import { Collection, fetchCollections } from '../services/booksApi';

interface ExplorePageProps {
  books: Book[];
  onBookSelect: (bookSlug: string, source?: string, collectionName?: string) => void;
  onNavigateToExplore?: () => void;
  onNavigateToLibrary?: () => void;
  onNavigateToProfile?: () => void;
  onNavigateToMyBooks?: () => void;
  onNavigateHome?: () => void;
  onNavigateToLogin?: (source?: string) => void;
  onNavigateToSignup?: (source?: string) => void;
  onToggleMenu: () => void;
  isAuthenticated: boolean;
  isLoading?: boolean;
}

const ExplorePage: React.FC<ExplorePageProps> = ({
  books,
  onBookSelect,
  onNavigateToExplore,
  onNavigateToLibrary,
  onNavigateToProfile,
  onNavigateToMyBooks,
  onNavigateHome,
  onNavigateToLogin,
  onNavigateToSignup,
  onToggleMenu,
  isAuthenticated,
  isLoading,
}) => {
  const { theme } = useTheme();
  const isDarkMode = theme === 'dark';
  const native = isNativeApp();
  // Copy for web visitors who haven't signed up; members already know the pitch.
  const showIntro = !native && !isAuthenticated;
  const [collections, setCollections] = useState<Collection[]>([]);
  const [collectionsLoading, setCollectionsLoading] = useState(true);

  useEffect(() => {
    const loadCollections = async () => {
      try {
        setCollectionsLoading(true);
        const collectionsData = await fetchCollections();
        setCollections(collectionsData);
      } catch (error) {
        console.error('Failed to load collections:', error);
      } finally {
        setCollectionsLoading(false);
      }
    };

    loadCollections();
  }, []);

  const handleBookClick = (book: Book, collectionName?: string) => {
    const matchingBook = books.find((candidate) => candidate.id === book.id) ?? book;
    const slug = titleToSlug(matchingBook.title);
    onBookSelect(slug, 'explore', collectionName);
  };

  const themeClasses = isDarkMode ? 'text-theme-primary-dark' : 'bg-theme-light text-theme-primary-light';
  return (
    <div
      className={`w-full transition-colors duration-300 ${themeClasses}`}
      style={{ backgroundColor: isDarkMode ? 'var(--background-colour-dark-mode)' : 'var(--background-colour-light-mode)' }}
    >
      <TopNavigation
        isDarkMode={isDarkMode}
        onToggleMenu={onToggleMenu}
        onNavigateHome={onNavigateHome}
        onNavigateToExplore={onNavigateToExplore}
        onNavigateToLibrary={onNavigateToLibrary}
        onNavigateToLogin={(source) => onNavigateToLogin?.(source)}
        onNavigateToSignup={(source) => onNavigateToSignup?.(source)}
        onNavigateToMyBooks={onNavigateToMyBooks}
        onNavigateToProfile={onNavigateToProfile}
        onBookSelect={onBookSelect}
        showProfileIcon={isAuthenticated}
        isLoading={isLoading}
        activeItem="explore"
      />

      <div className="w-full px-6 pt-12 sm:px-8 lg:px-16 xl:px-24 pb-[calc(10rem+var(--safe-bottom))]">
        {/* The app keeps this tight so a phone shows books sooner. On the web
            the content gets the same breathing room below the title whether
            or not the intro paragraph is showing. */}
        <header className={`mx-auto w-full max-w-6xl ${native ? 'mb-8' : 'mb-20'}`}>
          <h1
            className={`portrait-text-app mb-4 text-left text-4xl font-bold sm:text-5xl lg:text-6xl ${
              isDarkMode ? 'text-theme-primary-dark' : 'text-theme-primary-light'
            }`}
          >
            Explore our collections
          </h1>
          {/* Marketing blurb: sets the collection up for a first-time web
              visitor, but it is dead weight for the app's own users and for
              signed-in members, who already know what they came for. */}
          {showIntro && (
            <p
              className={`max-w-4xl pt-6 text-md leading-relaxed sm:text-lg md:text-xl ${
                isDarkMode ? 'text-theme-primary-dark' : 'text-blue-700'
              }`}
            >
              The legendary literary editor Barbara Epler has selected 100 books for Aleph, some of the best ever written. Too many books to choose from? Not sure where to start? Try a mood, a theme, or literary style.
            </p>
          )}
        </header>

        <section className="mx-auto w-full max-w-6xl">
          {collectionsLoading ? (
            <div className="rounded-3xl p-8">Loading collections…</div>
          ) : (
            <>
              {collections.map((collection) => (
                <CollectionRow
                  key={collection.name}
                  collection={collection}
                  onBookClick={(book) => handleBookClick(book, collection.name)}
                />
              ))}

              <div className="mt-6 text-center">
                <button
                  type="button"
                  onClick={onNavigateToLibrary}
                  className={`px-5 py-3 rounded-full text-base transition-all shadow-sm select-none cursor-pointer hover:shadow-md border ${
                    isDarkMode
                      ? 'border-blue-600 text-white bg-blue-600 hover:bg-blue-700'
                      : 'border-blue-600 text-blue-600 hover:bg-blue-600 hover:text-white'
                  }`}
                >
                  Browse our full collection
                </button>
              </div>
            </>
          )}
        </section>
      </div>
    </div>
  );
};

export default ExplorePage;
